import React, {
  useState,
  useRef,
  useEffect,
} from 'react'
import { useRouter } from 'next/router'
import classesOld from './Packages.styles'
import {
  Box, PropTypes, Typography,
} from '@material-ui/core'
import StyledButton, { ButtonProps } from '../StyledButton/StyledButton'
import { AnimationLine } from './AnimationLine'
import { cx } from 'linaria'
import {
  useOrderContext,
} from '../NewOrderFlow/OrderContext'
import useIntersectionObserver from '../EnquiryBottomBar.js/EnquiryHook'
import {
  HobsProduct,
} from '../../api/Packages'
import createCRMApiClient from '../../api/CRMApi'
import CheckIcon from '@material-ui/icons/Check'
import TypographyCMS, { TypographyProps } from '../Typography/Typography'
import Features, {
  BulletModalProps,
} from './Features'
import Price from './Price'
import {
  getDiscountPercent,
} from '../../utils/getProductDetails'
import { Flag } from './Flag'
import {
  BOX, fontWeights, COLORS,
} from '../../styles/theme'
import { withStyles } from '@material-ui/core/styles'
import { PackageStyles } from './Packages.styles2'
import { addToCartOptions } from './Helpers'
import { ChannelType } from '../../utils/commonEnums'
import { VoucherProps } from '../PaidMediaLandingPage/MediaPageHero/MediaPageVoucher'
import { MediaImageProps } from '../Media/MediaImage'
import StrikethroughName, { StrikethroughNameProps } from '../StrikethroughName/StrikethroughName'
import { useGeneralContext } from '../GeneralContext/GeneralContext'
import { FilterLabels } from './Packages'
import flashIcon from '../../../public/flash-sale-icon.svg'
import orangeFlashIcon from '../../../public/orange-flash-sale-icon.svg'
import { isPromotionActive, PromotionProps } from '../Promotions/Promotion'
import marketingEvents from '../../utils/marketing/marketingEvents'

export interface PackageProps extends HobsProduct {
  name?: string;
  price?: string;
  old_price?: any;
  simple_bullets?: string;
  bullets_back?: string;
  bullets_with_icons?: BulletModalProps[];
  bullet_align?: string;
  upload_speed?: string;
  download_speed?: string;
  hide?: boolean;
  out_of_contract_copy?: string;
  file_size?: number;
  button?: ButtonPropsExtended;
  button_secondary?: ButtonPropsExtended;
  readless_text?: string;
  flag?: FlagProps;
  product?: PackageProps;
  width?: string;
  startSpeedAnimation?: boolean;
  discount?: string;
  on_click_package?: Function;
  display_subtitle?: string;
  display_title?: string;
  selected?: boolean;
  term?: number;
  flip?: boolean;
  align?: PropTypes.Alignment;
  bundles?: PackageProps[];
  anchor?: string;
  title?: TypographyProps;
  footnote?: TypographyProps;
  type?: string;
  noContainer?: boolean;
  wier_id?: string;
  contract_length?: number;
  show_free_months?: boolean;
  mt?: number;
  classes?: any;
  hide_strikethrough?: boolean;
  usage_description?: string;
  speed?: string;
  display_image?: MediaImageProps;
  voucher?: VoucherProps[];
  promo_code?: string;
  free_months_with_promo_code?: string;
  after_promo_copy?: string;
  strikethrough_name?: StrikethroughNameProps;
  name_first_line?: string;
  name_second_line?: string;
  setDisableButton?: Function;
  disabledButton?: boolean;
  price_guidance?: string;
  free_set_up_copy?: string;
  activeTermLength?: number;
  filter_labels?: FilterLabels[];
  filter_labels_option_2?: FilterLabels[];
  filter_labels_option_3?: FilterLabels[];
  promotion_ga4?: PromotionProps;
  active_promotion_ga4?: PromotionProps;
}
export interface ButtonPropsExtended extends ButtonProps {
  text?: string;
}
export interface FlagProps {
  flag_text: string;
  subtitle?: string;
  background_color?: string;
  text_color?: string;
}

const Package = withStyles(PackageStyles)(({
  price,
  old_price,
  simple_bullets,
  bullets_back,
  out_of_contract_copy,
  bullets_with_icons,
  flag,
  upload_speed,
  file_size,
  hide,
  button,
  product,
  startSpeedAnimation,
  on_click_package,
  display_name,
  strikethrough_name,
  name_first_line,
  name_second_line,
  selected,
  term,
  button_secondary,
  flip,
  align = 'center',
  show_free_months,
  classes,
  hide_strikethrough,
  readless_text,
  voucher,
  promo_code,
  free_months_with_promo_code,
  after_promo_copy,
  setDisableButton,
  disabledButton,
  price_guidance,
  free_set_up_copy,
  activeTermLength,
  promotion_ga4,
}: PackageProps) => {
  const client = createCRMApiClient()
  const fileSize = file_size ? file_size : 500
  const routerPath = useRouter()
  // Check if the current path includes '/order/package'
  const isOrderPackagePage = routerPath.asPath.includes('/order/package')
  const {
    options, setOptions,
  } = useOrderContext()

  const {
    generalOptions,
  } = useGeneralContext()

  const { 
    premiseLevelPricingOffer, 
    premiseLevelPricingStartDate, 
    premiseLevelPricingEndDate 
  } = generalOptions

  const {
    utmSource,
  } = options

  const {
    generalSettingPage,
  } = generalOptions

  const applyPromoCode = Boolean(
    promo_code &&
    !(utmSource === 'awin' && generalSettingPage?.exclude_affiliates_from_promo),
  )

  const [
    open,
    setOpen,
  ] = useState(false)

  useEffect(() => {
    setOpen(false)
  }, [activeTermLength])

  const [
    activePromotion,
    setActivePromotion
  ] = useState<PromotionProps | null>(null)

  useEffect(() => {
    if (promotion_ga4 && isPromotionActive(promotion_ga4)) {
      setActivePromotion(promotion_ga4)
    }
  }, [promotion_ga4])

  const toggleReadMore = (event: React.ChangeEvent<{}>) => {
    event.stopPropagation()
    event.preventDefault()
    setOpen(!open)
  }

  const [
    hasValidPremiseLevelPricing,
    setHasValidPremiseLevelPricing,
  ] = useState(false)

  const [
    freeMonthsCount,
    setFreeMonthsCount,
  ] = useState<number | null>(null)

  const [
    formattedEndDate,
    setFormattedEndDate,
  ] = useState<string | null>(null)

  // Check for premise level pricing data from both sources:
  // 1. From the generalOptions context (set by SearchBar.tsx)
  // 2. From the Packages.tsx component (via router.query.sprn)
  useEffect(() => {
    // First check if PLPOffer is set to false in session storage
    try {
      const plpOfferValue = sessionStorage.getItem('PLPOffer')
      if (plpOfferValue === 'false') {
        // If PLPOffer is false, reset premise level pricing data
        console.log('PLPOffer is set to false in session storage, resetting premise level pricing in Package component')
        setHasValidPremiseLevelPricing(false)
        setFreeMonthsCount(null)
        setFormattedEndDate(null)
        return
      }
    } catch (err) {
      // Session storage not supported or error occurred
      console.error('Failed to check PLPOffer in session storage', err)
    }
    
    // If PLPOffer is not false, proceed with normal premise level pricing logic
    // First check if we have premise level pricing data in generalOptions
    if (premiseLevelPricingOffer) {
      const now = Date.now()
      const isValid = (!premiseLevelPricingStartDate || now >= new Date(premiseLevelPricingStartDate).getTime()) &&
                      (!premiseLevelPricingEndDate || now <= new Date(premiseLevelPricingEndDate).getTime())
      
      setHasValidPremiseLevelPricing(isValid)

      // Extract the number of free months from the offer string (e.g., "EXCLUSIVE_3_MONTHSFREE" -> 3)
      const monthsMatch = premiseLevelPricingOffer.match(/EXCLUSIVE_(\d+)_MONTHSFREE/)
      if (monthsMatch && monthsMatch[1]) {
        setFreeMonthsCount(parseInt(monthsMatch[1], 10))
      }

      // Format the end date for display
      if (premiseLevelPricingEndDate) {
        const endDate = new Date(premiseLevelPricingEndDate)
        const day = endDate.getDate()
        const month = endDate.toLocaleString('en-GB', { month: 'long' })
        setFormattedEndDate(`${day}${getDaySuffix(day)} ${month}`)
      }
      return
    }
    
    // If we don't have premise level pricing data in generalOptions,
    // check if we have a sprn parameter in the URL and fetch the data directly
    const { sprn } = routerPath.query
    if (sprn && !premiseLevelPricingOffer) {
      const fetchPremiseLevelPricing = async () => {
        try {
          const premisePricing = await client.premiseLevelPricing.get(Number(sprn))
          
          if (premisePricing && premisePricing.length > 0) {
            const pricingItem = premisePricing[0]
            
            // Check if the premise level pricing offer is valid
            const now = Date.now()
            const isValid = (!pricingItem.startdate || now >= new Date(pricingItem.startdate).getTime()) &&
                            (!pricingItem.enddate || now <= new Date(pricingItem.enddate).getTime())
            
            setHasValidPremiseLevelPricing(isValid)
            
            // Extract the number of free months from the offer string
            const monthsMatch = pricingItem.offer.match(/EXCLUSIVE_(\d+)_MONTHSFREE/)
            if (monthsMatch && monthsMatch[1]) {
              setFreeMonthsCount(parseInt(monthsMatch[1], 10))
            }
            
            // Format the end date for display
            if (pricingItem.enddate) {
              const endDate = new Date(pricingItem.enddate)
              const day = endDate.getDate()
              const month = endDate.toLocaleString('en-GB', { month: 'long' })
              setFormattedEndDate(`${day}${getDaySuffix(day)} ${month}`)
            }
          } else {
            setHasValidPremiseLevelPricing(false)
            setFreeMonthsCount(null)
            setFormattedEndDate(null)
          }
        } catch (error) {
          console.error('Error fetching premise level pricing:', error)
          setHasValidPremiseLevelPricing(false)
          setFreeMonthsCount(null)
          setFormattedEndDate(null)
        }
      }
      
      fetchPremiseLevelPricing()
    } else {
      setHasValidPremiseLevelPricing(false)
      setFreeMonthsCount(null)
      setFormattedEndDate(null)
    }
  }, [premiseLevelPricingOffer, premiseLevelPricingStartDate, premiseLevelPricingEndDate, routerPath.query])

  // Helper function to get the day suffix (st, nd, rd, th)
  const getDaySuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }
  
  const handleClick = async (e: React.ChangeEvent<{}>) => {
    e.stopPropagation()

    if (setDisableButton) {
      setDisableButton(true)
    }

    const channel = window.location.href.includes('utm_medium=affiliate') ? ChannelType.AFFILIATE : ChannelType.DIGITAL
    setOptions({
      ...options,
      channel,
    })

  // Only apply premise level pricing if it's valid (within date range)
  // hasValidPremiseLevelPricing is set based on date validation in the useEffect
  const promoCode = hasValidPremiseLevelPricing ? premiseLevelPricingOffer : promo_code
  
  // Double-check dates are valid before applying premise level pricing
  let finalPromoCode = promoCode;
  if (promoCode === premiseLevelPricingOffer) {
    const now = Date.now();
    const isValid = (!premiseLevelPricingStartDate || now >= new Date(premiseLevelPricingStartDate).getTime()) &&
                    (!premiseLevelPricingEndDate || now <= new Date(premiseLevelPricingEndDate).getTime());
    
    // Only use the premise level pricing if dates are valid
    finalPromoCode = isValid ? promoCode : promo_code;
  }
  
  const productWithPromoAndPromotion = product ? { 
    ...product, 
    promo_code: finalPromoCode,
    ...(activePromotion && { active_promotion_ga4: activePromotion })
    } : product

    if (activePromotion) {
      marketingEvents.promotionClicked({
        options,
        promotion: activePromotion,
        product: productWithPromoAndPromotion || product,
      })
    }
  
    const packageObj = {
      product: productWithPromoAndPromotion!,
      options,
      client,
      channel,
      hide_strikethrough,
      voucher,
    }

    if (on_click_package) {
      on_click_package(productWithPromoAndPromotion, channel, hide_strikethrough)
      return
    }

    if (button?.url && button?.target !== 'blank') {
      routerPath.push(routerPath.asPath + button?.url, undefined, { shallow: true })
    }

    const packageOptions = await addToCartOptions(packageObj)
    setOptions(packageOptions)
  }

  const flipCard = open ?
    cx(classesOld.flipcard.inner, classesOld.flipcard.cardFlipped) :
    cx(classesOld.flipcard.inner)

  const packageCards = useRef<any>(product?.productOfferingId)
  const [isVisible] = useIntersectionObserver({
  elementRef: packageCards,
  horizontalOnly:true
})

  const discount_percent = getDiscountPercent(product!)
  const getAnnualSaving = (price: string, discount: { percent: number; amount: number }): string => {
    const total = Number(price) / 100 * discount.percent * discount.amount
    return `Save £${total.toFixed(2)
      .toString()}`
  }

  const showstrikethroughName = strikethrough_name && Object.values(strikethrough_name).length > 0 && name_first_line && name_second_line
  const leftSpacingCalc = strikethrough_name?.name_image?.width ? (strikethrough_name?.name_image?.width / 8) + 1 : 0
  const outOfContractPrice = out_of_contract_copy || 'Just £4* more when out of contract'
  const priceGuidance = price_guidance || '/month*'
  
  const preconfiguredFlag = () => {
    if (hasValidPremiseLevelPricing && isOrderPackagePage!) {
      return {
        text: "Your exclusive offer",
        position: "center",
        background_color: `${COLORS.orange1}`,
        text_color: `${COLORS.white}`,
        image: <img src={flashIcon} alt="flash sale icon" width="24" height="24" />,
      }
    }

    if (options?.promoToApply! && !promo_code) {
      return {
        text: 'First month free',
        position: 'right',
        background_color: `${COLORS.tertiary}`,
        text_color: `${COLORS.primary}`,
      }
    }

    return null
  }

  const cardContent = () => {
    return (
      <>
        {showstrikethroughName &&
        <>
          <Box display="flex">
            <StrikethroughName {...strikethrough_name}/>
            <Typography variant="h4" style={{ color: `${strikethrough_name?.color}` }}>{name_first_line}</Typography>
          </Box>
          <TypographyCMS
            variant="h4"
            margin_bottom={1.5}
            margin_left={leftSpacingCalc}
            text={name_second_line}
            color="primary"
          />
        </>}
        {display_name && !showstrikethroughName &&
          <TypographyCMS
            align={align}
            variant="h4"
            margin_bottom={1.5}
            text={display_name}
            color="primary"
          />}
        <Price
          price={applyPromoCode || hasValidPremiseLevelPricing && isOrderPackagePage ? '0' : price}
          old_price={old_price}
          price_guidance={
            hasValidPremiseLevelPricing && isOrderPackagePage && freeMonthsCount 
              ? ` for ${freeMonthsCount} months` 
              : applyPromoCode 
                ? free_months_with_promo_code 
                : priceGuidance
          }
          product={product!}
          align={align}
          term={term}
          discount_full
          show_free_months={show_free_months}
          hide_strikethrough={hide_strikethrough || applyPromoCode || hasValidPremiseLevelPricing && isOrderPackagePage}
          after_price_copy={
            (applyPromoCode || hasValidPremiseLevelPricing && isOrderPackagePage) 
              ? `/month until March ${new Date().getFullYear() + 1}`
              : (applyPromoCode ? after_promo_copy : undefined)
          }
        />
        <Box mt={2}>
          <AnimationLine
            fileSize={fileSize}
            speed={upload_speed}
            startSpeedAnimation={startSpeedAnimation && isVisible}
          />
        </Box>
        <Box flexGrow="1">
          <Features
            mt={2}
            bullets_with_icons={bullets_with_icons}
            simple_bullets={simple_bullets}
            bullets_crm={product} // Setup fee
            free_set_up_copy={free_set_up_copy}
            bullet_size="large"
            text_color="textPrimary"
            isVisible={isVisible}
            bullet_offer_ends={hasValidPremiseLevelPricing && isOrderPackagePage && formattedEndDate ? {
              text: `Offer ends ${formattedEndDate}`,
              icon: orangeFlashIcon
            } : undefined}
          />
        </Box>
        { cardCTA() }
      </>
    )
  }

  const cardFront = () => {
    return (
      <Box
        className={cx(classes.package, classes.commonPackage, classesOld.flipcard.front)}
        style={{ 
          borderColor: selected ? COLORS.secondary : (hasValidPremiseLevelPricing && isOrderPackagePage ? COLORS.orange1 : COLORS.gray2),
          paddingTop: 40,
        }}
        {...BOX}
      >
        {preconfiguredFlag() ?
          <Flag
            {...preconfiguredFlag()}
            position={preconfiguredFlag()?.position as 'left' | 'right' | 'center'}
          /> :
          ((flag && flag.flag_text) || discount_percent) &&
          <Flag
            text_color={flag?.text_color}
            background_color={flag?.background_color}
            text={discount_percent ? getAnnualSaving(price!, discount_percent) : flag?.flag_text}
            position="right"
            subtitle={flag?.subtitle}
          />}
        {cardContent()}
      </Box>
    )
  }

  const cardBack = () => {
    return (
      <Box
        className={cx(classes.package, classes.commonPackage, classesOld.flipcard.back)}
        {...BOX}
      >
        {preconfiguredFlag() ?
          <Flag
            {...preconfiguredFlag()}
            position={preconfiguredFlag()?.position as 'left' | 'right'}
          /> :
          ((flag && flag.flag_text) || discount_percent) &&
          <Flag
            text_color={flag?.text_color}
            background_color={flag?.background_color}
            text={discount_percent ? getAnnualSaving(price!, discount_percent) : flag?.flag_text}
            position="left"
            subtitle={flag?.subtitle}
          />}
        {display_name &&
          <TypographyCMS align={align} variant="h4" margin_bottom={1.5} text={display_name} color="primary"/>}
        <Box flexGrow="1">
          <Features
            simple_bullets={bullets_back}
            bullets_crm={product}
            bullet_out_of_contract_price={outOfContractPrice}
            bullet_size="large"
            mt={3}
            text_color="textPrimary"
          />
        </Box>
        { cardCTA(readless_text || 'Twist back') }
      </Box>
    )
  }

  const cardCTA = (secondaryButtonText?: string) => {
    if ((button && button.text) || (button_secondary && button_secondary.text)) {
      return (
        <Box component="nav" mt={4} width="100%" display="flex" flexDirection="column" alignItems="center">
          {button && button.text &&
            <StyledButton
              color="primary"
              handleClick={handleClick}
              isFullWidth
              url={button.url}
              target={button.target}
              disabled={selected || disabledButton}
              className={cx(selected && classesOld.selected)}
              ariaHidden={isVisible}
              tabIndex={isVisible ? 0 : -1}
            >
              {selected && <CheckIcon/>}
              {selected ? 'Selected' : button.text}
            </StyledButton>}
          {button_secondary && button_secondary.text &&
            <button
              type="button"
              onClick={flip ? toggleReadMore : (e: React.ChangeEvent<{}>) => e.preventDefault()}
              className={classesOld.twistButton}
              aria-hidden={!isVisible}
              tabIndex={isVisible ? 0 : -1}
            >
              <Typography
                variant="body1"
                color="secondary"
                style={{
                  textDecoration: 'underline',
                  fontWeight: fontWeights.bold,
                }}
              >
                {secondaryButtonText || button_secondary.text}
              </Typography>
            </button>}
        </Box>
      )
    }

    return null
  }

  return (
    <div className={cx(hide ? classesOld.hide : flipCard)} ref={packageCards} id={product?.anchor}>
      { cardFront() }
      { flip && cardBack() }
    </div>
  )
})

export default Package
